import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, ArrowLeft, Heart, Upload, X, Image, Loader, Trash2, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DreamGallery({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [blobUrls, setBlobUrls] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(blobUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const fetchGallery = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/gallery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(res.data);
    } catch {
      toast.error(t("gallery.loadError") || "Gallerian lataus epäonnistui");
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const loadImage = useCallback(async (itemId) => {
    if (blobUrls[itemId] || loadingImages[itemId]) return;
    setLoadingImages(prev => ({ ...prev, [itemId]: true }));
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/gallery/${itemId}/image`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const blobUrl = URL.createObjectURL(res.data);
      setBlobUrls(prev => ({ ...prev, [itemId]: blobUrl }));
    } catch {
      // skip
    } finally {
      setLoadingImages(prev => ({ ...prev, [itemId]: false }));
    }
  }, [blobUrls, loadingImages]);

  useEffect(() => {
    items.forEach(item => {
      if (!blobUrls[item.id] && !loadingImages[item.id]) {
        loadImage(item.id);
      }
    });
  }, [items]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("gallery.onlyImages") || "Vain kuvatiedostot sallittu");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("gallery.tooLarge") || "Kuva on liian suuri (max 5MB)");
      return;
    }
    setUploadFile(file);
    setUploadPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("title", uploadTitle || "Unitaide");
      await axios.post(`${API}/gallery`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success(t("gallery.uploadSuccess") || "Kuva jaettu galleriaan!");
      setShowUpload(false);
      setUploadFile(null);
      if (uploadPreview) URL.revokeObjectURL(uploadPreview);
      setUploadPreview(null);
      setUploadTitle("");
      fetchGallery();
    } catch {
      toast.error(t("gallery.uploadError") || "Kuvan lataus epäonnistui");
    } finally {
      setUploading(false);
    }
  };

  const handleLike = async (itemId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/gallery/${itemId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(prev => prev.map(item =>
        item.id === itemId
          ? { ...item, likes: res.data.likes, has_liked: res.data.has_liked }
          : item
      ));
    } catch {
      toast.error(t("gallery.likeError") || "Tykkäys epäonnistui");
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm(t("gallery.confirmDelete") || "Poistetaanko kuva galleriasta?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/gallery/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(prev => prev.filter(item => item.id !== itemId));
      if (blobUrls[itemId]) {
        URL.revokeObjectURL(blobUrls[itemId]);
        setBlobUrls(prev => {
          const copy = { ...prev };
          delete copy[itemId];
          return copy;
        });
      }
      toast.success(t("gallery.deleted") || "Kuva poistettu");
    } catch {
      toast.error(t("gallery.deleteError") || "Poisto epäonnistui");
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("fi-FI", { day: "numeric", month: "short" });
    } catch { return ""; }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-dream-light via-white to-dream-light">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-white/30 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/community-hub")} className="p-2 hover:bg-white/50 rounded-xl transition-colors" data-testid="gallery-back">
              <ArrowLeft className="w-5 h-5 text-void" />
            </button>
            <h1 className="text-xl font-heading font-light text-void tracking-wide">
              {t("gallery.title")}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/settings")} className="p-2 hover:bg-white/50 rounded-xl transition-colors">
              <Settings className="w-5 h-5 text-void/70" />
            </button>
            <button onClick={onLogout} className="p-2 hover:bg-white/50 rounded-xl transition-colors">
              <LogOut className="w-5 h-5 text-void/70" />
            </button>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-heading font-light text-void tracking-tight mb-3">
            {t("gallery.heading")}
          </h2>
          <p className="text-void/60 font-body max-w-lg mx-auto">
            {t("gallery.subtitle")}
          </p>
        </div>

        {/* Upload Button */}
        <div className="text-center mb-8">
          <Button
            onClick={() => setShowUpload(true)}
            className="bg-void text-white hover:bg-void/90 rounded-full px-6 py-5 font-body font-medium transition-all hover:scale-105"
            data-testid="gallery-upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {t("gallery.share")}
          </Button>
        </div>

        {/* Upload Modal */}
        {showUpload && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
            <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-heading text-void">{t("gallery.shareTitle")}</h3>
                <button onClick={() => { setShowUpload(false); if (uploadPreview) URL.revokeObjectURL(uploadPreview); setUploadFile(null); setUploadPreview(null); }} className="p-2 hover:bg-gray-100 rounded-xl" data-testid="gallery-upload-close">
                  <X className="w-5 h-5 text-void/70" />
                </button>
              </div>

              {uploadPreview ? (
                <div className="relative mb-4">
                  <img src={uploadPreview} alt="Preview" className="w-full rounded-2xl max-h-64 object-cover" />
                  <button
                    onClick={() => { URL.revokeObjectURL(uploadPreview); setUploadFile(null); setUploadPreview(null); }}
                    className="absolute top-2 right-2 p-1.5 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-void/20 rounded-2xl p-8 flex flex-col items-center gap-3 hover:border-void/40 hover:bg-void/5 transition-colors mb-4"
                  data-testid="gallery-file-select"
                >
                  <Image className="w-10 h-10 text-void/30" />
                  <p className="text-sm text-void/60 font-body">{t("gallery.selectImage")}</p>
                  <p className="text-xs text-void/40 font-body">PNG, JPG, WEBP — max 5MB</p>
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="gallery-file-input"
              />

              <Input
                placeholder={t("gallery.titlePlaceholder")}
                value={uploadTitle}
                onChange={e => setUploadTitle(e.target.value)}
                className="mb-4 rounded-xl h-12 bg-gray-50 border-transparent focus:border-void/30"
                data-testid="gallery-title-input"
              />

              <Button
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
                className="w-full bg-void text-white hover:bg-void/90 rounded-full py-5 font-body font-medium"
                data-testid="gallery-submit"
              >
                {uploading ? (
                  <><Loader className="w-4 h-4 mr-2 animate-spin" /> {t("common.loading") || "Ladataan..."}</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> {t("gallery.upload")}</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Gallery Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader className="w-8 h-8 animate-spin text-void/30" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <Image className="w-16 h-16 text-void/20 mx-auto mb-4" />
            <p className="text-void/50 font-body">{t("gallery.empty")}</p>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
            {items.map(item => (
              <div
                key={item.id}
                className="break-inside-avoid glass-effect rounded-2xl overflow-hidden group"
                data-testid={`gallery-item-${item.id}`}
              >
                {blobUrls[item.id] ? (
                  <img
                    src={blobUrls[item.id]}
                    alt={item.title}
                    className="w-full cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setSelectedImage(item)}
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-48 bg-void/5 flex items-center justify-center">
                    <Loader className="w-6 h-6 animate-spin text-void/20" />
                  </div>
                )}
                <div className="p-4">
                  <p className="font-heading text-void text-sm font-light mb-1">{item.title}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleLike(item.id)}
                        className="flex items-center gap-1.5 group/like"
                        data-testid={`gallery-like-${item.id}`}
                      >
                        <Heart className={`w-4 h-4 transition-colors ${item.has_liked ? 'fill-red-400 text-red-400' : 'text-void/30 group-hover/like:text-red-400'}`} />
                        <span className="text-xs text-void/50 font-body">{item.likes || 0}</span>
                      </button>
                      <span className="text-xs text-void/40 font-body">{item.user_name}</span>
                    </div>
                    {(item.user_id === user?.id || user?.is_admin) && (
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded-lg transition-all"
                        data-testid={`gallery-delete-${item.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Fullscreen Image Modal */}
        {selectedImage && blobUrls[selectedImage.id] && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="gallery-fullscreen-modal"
          >
            <div className="relative max-w-4xl max-h-[90vh]">
              <img
                src={blobUrls[selectedImage.id]}
                alt={selectedImage.title}
                className="max-w-full max-h-[85vh] rounded-2xl object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent rounded-b-2xl p-4">
                <p className="text-white font-heading text-lg">{selectedImage.title}</p>
                <p className="text-white/70 font-body text-sm">{selectedImage.user_name}</p>
              </div>
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-3 right-3 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                data-testid="gallery-fullscreen-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
